/**
 * Storage Image Utilities - Hybrid Thumbnail Strategy
 * Phase 1: Pre-generated thumbnails (client-side on upload)
 * Phase 2: CDN transform fallback (for old photos without thumbnails)
 */

import { supabase } from '@/integrations/supabase/client';

/**
 * Extract storage path from public URL
 */
export function extractStoragePath(publicUrl: string, bucket: string = 'diary-photos'): string {
  const regex = new RegExp(`/storage/v1/object/public/${bucket}/(.+)$`);
  const match = publicUrl.match(regex);
  return match ? match[1] : publicUrl;
}

/**
 * Get Supabase CDN transformed URL with caching
 */
export function getTransformedPublicUrl(
  publicUrl: string,
  options: {
    bucket?: string;
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'contain' | 'cover' | 'fill';
    format?: 'origin' | 'webp';
  } = {}
): string {
  const {
    bucket = 'diary-photos',
    width = 400,
    height = 300,
    quality = 80,
    resize = 'contain',
    format = 'origin'
  } = options;

  const rawPath = extractStoragePath(publicUrl, bucket);
  const path = decodeURIComponent(rawPath);
  
  const { data } = supabase.storage
    .from(bucket)
    .getPublicUrl(path, {
      transform: { width, height, quality, resize, format: format as any }
    });

  return data.publicUrl;
}

/**
 * Smart photo display URL with fallback strategy
 * Priority 1: Pre-generated thumbnail (instant load)
 * Priority 2: CDN transform fallback (for old photos)
 */
export function getPhotoDisplaySrc(photo: {
  thumbnail_url?: string | null;
  src: string;
  src_size_bytes?: number | null;
}): string {
  // Priority 1: Pre-generated thumbnail exists
  if (photo.thumbnail_url) {
    return photo.thumbnail_url;
  }

  // Priority 2: CDN transform fallback for old photos without thumbnails
  return getTransformedPublicUrl(photo.src, {
    width: 400,
    height: 300,
    quality: 80,
    resize: 'contain'
  });
}

/**
 * Smart spec file display URL with fallback strategy
 * Priority 1: Pre-generated thumbnail (instant load)
 * Priority 2: CDN transform fallback (for old image files without thumbnails)
 * Priority 3: Original file (for non-images)
 */
export function getSpecFileDisplaySrc(file: {
  thumbnail_url?: string | null;
  file_url: string;
  file_type?: string | null;
}): string {
  // Only apply to images
  if (file.file_type?.startsWith('image/')) {
    // Priority 1: Pre-generated thumbnail exists
    if (file.thumbnail_url) {
      return file.thumbnail_url;
    }

    // Priority 2: CDN transform fallback (for old images uploaded before migration)
    return getTransformedPublicUrl(file.file_url, {
      width: 200,
      height: 200,
      quality: 80,
      resize: 'cover'
    });
  }

  // Non-images: return original URL (will be replaced by icon in UI)
  return file.file_url;
}

/**
 * Get signed URL with transform for non-public buckets (e.g., stakeholder-attachments)
 */
export async function getTransformedSignedUrl(
  bucket: string,
  path: string,
  expiresIn: number = 3600,
  options: {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'contain' | 'cover' | 'fill';
    format?: 'origin' | 'webp';
  } = {}
): Promise<string> {
  const {
    width = 80,
    height = 80,
    quality = 70,
    resize = 'cover',
    format = 'origin'
  } = options;

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn, {
      transform: { width, height, quality, resize, format: format as any }
    });

  if (error) throw error;
  return data.signedUrl;
}

/** Ảnh spec thu nhỏ — bucket suy từ URL (spec-images | diary-photos), cùng quy tắc thẻ Library.
 *  KHÔNG truyền `format`: để Supabase tự trả WebP (ép 'origin' làm PNG giữ nguyên → 600px vẫn ~750 kB, decode chậm 5-12 s).
 *  Không dùng getTransformedPublicUrl vì hàm đó luôn gửi format (mặc định 'origin'). Luôn truyền cả width lẫn height. */
export function specImageSrc(
  url: string | null | undefined, width: number, height: number,
  resize: 'contain' | 'cover' = 'contain',
  quality: number = 75,
): string | null {
  if (!url) return null;
  const bucket = url.includes('/spec-images/') && !url.includes('/diary-photos/') ? 'spec-images' : 'diary-photos';
  const path = decodeURIComponent(extractStoragePath(url, bucket));
  const { data } = supabase.storage.from(bucket).getPublicUrl(path, {
    transform: { width, height, quality, resize },
  });
  return data.publicUrl;
}

/** URL public Supabase Storage (bucket bất kỳ) → URL transform. Không phải URL storage public → trả nguyên. Dùng cho thumbnail chưa có thumbnail_url. */
export function publicStorageThumb(
  url: string | null | undefined, width: number, height: number,
  resize: 'contain' | 'cover' = 'contain', quality: number = 75,
  widthOnly: boolean = false,
): string | null {
  if (!url) return null;
  const m = url.match(/\/storage\/v1\/object\/public\/([^/]+)\/(.+)$/);
  if (!m) return url;
  const path = decodeURIComponent(m[2].split('?')[0]);
  const tf = widthOnly ? { width, resize, quality } : { width, height, resize, quality };
  return supabase.storage.from(m[1]).getPublicUrl(path, { transform: tf }).data.publicUrl;
}
